import * as c from '../common.js';
import * as bd from './boat-data.js';
/*
polarStruct format, can contain nulls or missing values in the boatspeed array
{
  tws: [4, 8, 12],
  twa: [
    { twa: 40, boatSpeed: [3.0, 4.0, 5.0] },
    { twa: 60, boatSpeed: [4.0, 5.0, 6.0] },
    { twa: 90, boatSpeed: [5.0, 6.0, 7.0] }
  ]
}
*/
export class Polar {
  constructor(polarStruct, fileName, fileId) {
    this.polarStruct = polarStruct;
    this.fileName = fileName || null;
    this.fileId = fileId || null;
  }

  static loadFromJSON(text, fileName, fileId) {
    const polarStruct = JSON.parse(text);
    for (let i = 0; i < polarStruct.twa.length; i++) {
      for (let j = 0; j < polarStruct.twa[i].boatSpeed.length; j++) {
        const v = polarStruct.twa[i].boatSpeed[j];
        if (v === null || v === undefined || v === "" || v === 0) {
          polarStruct.twa[i].boatSpeed[j] = null;
        } else {
          const num = parseFloat(v);
          polarStruct.twa[i].boatSpeed[j] = isNaN(num) ? null : num;
        }
      }
    }
    return new Polar(polarStruct, fileName, fileId);
  }

  static loadFromText(text, fileName, fileId) {
    const lines = text.trim().split('\n');
    const [header, ...twsStrings] = lines[0].split(';');
    let twsValues = twsStrings.map(parseFloat);
    twsValues = twsValues.filter(x => !isNaN(x));
    const polarStruct = {
      tws: twsValues,
      twa: []
    };
    
    for (let i = 1; i < lines.length; i++) {
      const fields = lines[i].split(';');
      const twa = Number(fields[0]);
      const speeds = fields.slice(1).map(Number);
      const twaStruct = {
        twa: twa,
        boatSpeed: []
      };
      for (let j = 0; j < twsValues.length; j++) {
        const v = speeds[j];
        if (v === 0 || isNaN(v)) {
          twaStruct.boatSpeed.push(null);
        } else {
          twaStruct.boatSpeed.push(v);
        }
      }
      polarStruct.twa.push(twaStruct);
    }
    return new Polar(polarStruct, fileName, fileId);
  }

  static createFromTimeframes(timeFrames, halfPolar) {
    const WIND_ANGLE_SERIES = [20,25,30,35,40,45,50,60,70,80,90,100,110,120,130,140,150,160,170,180,190,200,210,220,230,240,250,260,270,280,290,300,310,315,320,325,330,335,340];
    const WIND_SERIES = [2, 4, 6, 8, 10, 12, 14, 16, 20, 25, 30, 40, 50];

    const polarMap = {};
    WIND_ANGLE_SERIES.forEach(angle => {
      if (!halfPolar || angle < 180) {
        polarMap[angle] = {};
        WIND_SERIES.forEach(speed => {
          if (speed < 50)
            polarMap[angle][speed] = [];
        });
      }
    });

    for (const f of timeFrames) {
      let angle = f.twa;
      if (halfPolar && f.twa > 180)
        angle = (360 - f.twa);
      const dirBin = c.binValueWind(angle);
      const spdBin = c.binValueWindSpd(f.tws);
      if (!isNaN(dirBin) && !isNaN(spdBin) && polarMap[dirBin] && polarMap[dirBin][spdBin]) {
        polarMap[dirBin][spdBin].push(parseFloat(f.bs));
      }
    }

    const polarStruct = Polar.polarMapToStruct(polarMap);
    return new Polar(polarStruct, null, null);
  }

  static polarMapToStruct(polarMap, minDataPoints = 10) {
    const speedBins = new Set();
    Object.values(polarMap).forEach(dirObj => {
      Object.keys(dirObj).forEach(speedBin => speedBins.add(speedBin));
    });
    const sortedSpdBins = [...speedBins].sort((a, b) => parseInt(a) - parseInt(b));
  
    const polar = {};
    polar.tws = sortedSpdBins.map(x => parseInt(x));
    polar.twa = [];
    Object.keys(polarMap).sort((a, b) => parseInt(a) - parseInt(b)).forEach(twa => {
      const twaLine = {};
      twaLine.twa = parseInt(twa);
      twaLine.boatSpeed = [];
      sortedSpdBins.forEach(tws => {
        const dirData = polarMap[twa];
        if (dirData[tws] && dirData[tws].length >= minDataPoints) {
          const boatSpeed = c.median(dirData[tws]);
          twaLine.boatSpeed.push(parseFloat(boatSpeed)); 
        } else {
          twaLine.boatSpeed.push(null); 
        }
      });
      polar.twa.push(twaLine);
    });
    return polar;
  }

  serialiseToJSON() {
    return JSON.stringify(this.polarStruct, null, 2);
  }

  serialiseToText() {
    let polText = "TWA\\\\TWS;";
    this.polarStruct.tws.forEach(tws => {
      polText += tws + ";";
    });
    polText += "\n";
    for (let angleIdx = 0; angleIdx < this.polarStruct.twa.length; angleIdx++) {
      polText += this.polarStruct.twa[angleIdx].twa + ';';
      for (let windIdx = 0; windIdx < this.polarStruct.tws.length; windIdx++) {
        let val = this.polarStruct.twa[angleIdx].boatSpeed[windIdx];
        val = val == null ? 0 : val;
        polText += val + ';';
      }
      polText += "\n";
    }
    return polText;
  }

  
  interpolateSpeed(twa, tws) {
    if (!this.polarStruct || !this.polarStruct.twa || !this.polarStruct.tws) {
      return null;
    }

    twa = Math.abs(bd.convert360North0To180PosNeg(twa));

    const twsArray = this.polarStruct.tws;
    const twaArray = this.polarStruct.twa;

    if (twa < twaArray[0].twa || twa > twaArray[twaArray.length - 1].twa) {
      return null;
    }
    if (tws < twsArray[0] || tws > twsArray[twsArray.length - 1]) {
      return null;
    }

    let twaIdx1 = -1, twaIdx2 = -1;
    for (let i = 0; i < twaArray.length - 1; i++) {
      if (twa >= twaArray[i].twa && twa <= twaArray[i + 1].twa) {
        twaIdx1 = i;
        twaIdx2 = i + 1;
        break;
      }
    }
    if (twaIdx1 === -1) {
      twaIdx1 = twaIdx2 = twaArray.length - 1;
    }

    let twsIdx1 = -1, twsIdx2 = -1;
    for (let i = 0; i < twsArray.length - 1; i++) {
      if (tws >= twsArray[i] && tws <= twsArray[i + 1]) {
        twsIdx1 = i;
        twsIdx2 = i + 1;
        break;
      }
    }
    if (twsIdx1 === -1) {
      twsIdx1 = twsIdx2 = twsArray.length - 1;
    }

    const speed11 = twaArray[twaIdx1].boatSpeed[twsIdx1];
    const speed12 = twaArray[twaIdx1].boatSpeed[twsIdx2];
    const speed21 = twaArray[twaIdx2].boatSpeed[twsIdx1];
    const speed22 = twaArray[twaIdx2].boatSpeed[twsIdx2];

    if (speed11 === null || speed12 === null || speed21 === null || speed22 === null) {
      return null;
    }

    const twaFrac = twaIdx1 === twaIdx2 ? 0 : 
      (twa - twaArray[twaIdx1].twa) / (twaArray[twaIdx2].twa - twaArray[twaIdx1].twa);
    const twsFrac = twsIdx1 === twsIdx2 ? 0 : 
      (tws - twsArray[twsIdx1]) / (twsArray[twsIdx2] - twsArray[twsIdx1]);

    const speed1 = speed11 * (1 - twsFrac) + speed12 * twsFrac;
    const speed2 = speed21 * (1 - twsFrac) + speed22 * twsFrac;
    const interpolatedSpeed = speed1 * (1 - twaFrac) + speed2 * twaFrac;

    return interpolatedSpeed;
  }
}
